import type { LucideIcon } from "lucide-react"
import { ArrowLeft, ChevronRight } from "lucide-react"
import { createElement, Fragment, useEffect, useRef } from "react"
import { useTranslation } from "react-i18next"

import { DirtyDraftLink } from "@/components/shared/dirty-draft-navigation"
import { buttonVariants } from "@/components/ui/button-variants"

// An ancestor crumb. A record ancestor carries its type, which reads after
// the name in brackets, "Science Faculty (Team)", in a lighter weight; the
// whole is one link's text.
export type Crumb = { label: string; href: string; type?: string }

// The older ancestors a long trail folds away, read as one non-interactive
// "…" between the crumbs before and after it. It never ends a trail, so it
// is never the parent Back returns to.
export type FoldedCrumb = { folded: true }

export type TrailCrumb = Crumb | FoldedCrumb

const isFolded = (crumb: TrailCrumb): crumb is FoldedCrumb => "folded" in crumb

export type CurrentCrumb = {
  icon: LucideIcon
  label: string
  // When the page has no identity row its last crumb is the level-one
  // heading, and carries the id a form points its `aria-labelledby` at.
  heading: boolean
  titleId?: string
}

// Back, then the trail from the root to the parent, then the current page
// with its icon. Back is the parent crumb as a link, so following either is
// an ordinary visit that the browser's own back still undoes; on a page
// guarding a dirty form, both ask before discarding it. A root page has no
// parent, so no Back, and is its own only crumb. Back stays put while the
// trail scrolls rather than wraps, so a deep trail keeps both Back and the
// current page reachable on a phone.
export function BreadcrumbStrip({
  crumbs,
  current,
}: {
  crumbs: TrailCrumb[]
  current: CurrentCrumb
}) {
  const { t } = useTranslation("nav")
  const last = crumbs.at(-1)
  const parent = last === undefined || isFolded(last) ? undefined : last
  const trail = useRef<HTMLOListElement>(null)

  // A trail wider than its strip shows its end, the current page, and the
  // root is what scrolling reveals. The list's own box is fixed by the strip,
  // so the crumbs are what the observer watches: they widen when the web
  // font swaps in after mount, and the anchor is re-applied.
  useEffect(() => {
    const list = trail.current
    if (list === null) return

    const showEnd = () => {
      list.scrollLeft = list.scrollWidth
    }
    showEnd()
    const observer = new ResizeObserver(showEnd)
    observer.observe(list)
    for (const crumb of list.children) observer.observe(crumb)
    return () => observer.disconnect()
  }, [crumbs, current.label])

  const currentContent = (
    <>
      {createElement(current.icon, { className: "size-4 shrink-0" })}
      <span id={current.heading ? current.titleId : undefined}>
        {current.label}
      </span>
    </>
  )
  const currentClasses = "inline-flex items-center gap-1.5 font-bold text-black"

  return (
    <nav
      aria-label={t("breadcrumbs")}
      data-testid="page-chrome-strip"
      className="flex min-w-0 flex-1 items-center gap-3"
    >
      {parent !== undefined && (
        <DirtyDraftLink
          href={parent.href}
          data-testid="page-chrome-back"
          className={buttonVariants({ size: "sm", className: "shrink-0" })}
        >
          <ArrowLeft />
          {t("back")}
        </DirtyDraftLink>
      )}
      <ol
        ref={trail}
        data-testid="page-chrome-trail"
        className="flex min-w-0 items-center gap-1 overflow-x-auto text-sm whitespace-nowrap [scrollbar-width:none]"
      >
        {crumbs.map((crumb, index) => (
          <Fragment
            key={
              isFolded(crumb)
                ? `folded:${index}`
                : `${crumb.label}:${crumb.href}`
            }
          >
            <li className="flex shrink-0 items-center">
              {isFolded(crumb) ? (
                <span
                  data-testid="page-chrome-folded"
                  className="text-foreground/70"
                >
                  …
                </span>
              ) : (
                <DirtyDraftLink
                  href={crumb.href}
                  className="underline-offset-4 hover:underline"
                >
                  {crumb.label}
                  {crumb.type !== undefined && (
                    <span className="text-foreground/70"> ({crumb.type})</span>
                  )}
                </DirtyDraftLink>
              )}
            </li>
            <ChevronRight
              aria-hidden
              className="text-muted-foreground size-4 shrink-0"
            />
          </Fragment>
        ))}
        <li
          aria-current="page"
          data-testid="page-chrome-current"
          className="flex shrink-0 items-center"
        >
          {current.heading ? (
            <h1 className={currentClasses}>{currentContent}</h1>
          ) : (
            <span className={currentClasses}>{currentContent}</span>
          )}
        </li>
      </ol>
    </nav>
  )
}
